// Economic Indicators Service - Yahoo Finance & Investpy Integration
import type { EconomicIndicator, MarketCorrelation, EconomicInsight } from '../types/economicIndicators';
import { yahooFinanceService } from './yahooFinanceService';
import { investpyService } from './investpyService';

// Data source type
type DataSource = 'yahoo' | 'investpy' | 'mock';

// Economic Indicators Configuration for Yahoo Finance & Investpy
const ECONOMIC_INDICATORS = {
  '10Y1': {
    symbol: '10Y1',
    name: '10-Year Yield Futures',
    yahoo: '^TNX', // 10-Year Treasury Yield
    investpy: 'us-10-year-bond-yield',
    description: 'US 10-Year Treasury Note Futures - Key interest rate benchmark',
    correlations: {
      currencies: ['DXY', 'EUR', 'GBP', 'JPY'],
      commodities: ['GOLD', 'SILVER'],
      indices: ['SP500', 'NASDAQ', 'DOW']
    }
  },
  'ZN1': {
    symbol: 'ZN1',
    name: 'Futures T-Note 10 Years',
    yahoo: 'ZN=F', // 10-Year T-Note Futures
    investpy: 'us-10-year-note',
    description: 'Chicago Board of Trade 10-Year T-Note Futures',
    correlations: {
      currencies: ['DXY', 'EUR', 'CHF'],
      commodities: ['GOLD', 'PLATINUM'],
      indices: ['SP500', 'DOW']
    }
  },
  'DXY': {
    symbol: 'DXY',
    name: 'US Dollar Index',
    yahoo: 'DX-Y.NYB', // US Dollar Index
    investpy: 'us-dollar-index',
    description: 'Measures USD strength against basket of major currencies',
    correlations: {
      currencies: ['EUR', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'NZD'],
      commodities: ['GOLD', 'SILVER', 'CRUDE', 'COPPER'],
      indices: ['SP500', 'NASDAQ']
    }
  },
  'VX1': {
    symbol: 'VX1',
    name: 'CBOE Volatility Index',
    yahoo: '^VIX', // CBOE Volatility Index
    investpy: 'volatility-s-p-500',
    description: 'Market fear gauge - measures expected volatility',
    correlations: {
      currencies: ['JPY', 'CHF'],
      commodities: ['GOLD'],
      indices: ['SP500', 'NASDAQ', 'DOW']
    }
  },
  'USEPU': {
    symbol: 'USEPU',
    name: 'Economic Policy Uncertainty Index',
    yahoo: 'VXX', // iPath Series B S&P 500 VIX Short-Term Futures ETN
    investpy: 'vxx-etf',
    description: 'Measures economic policy uncertainty in the United States',
    correlations: {
      currencies: ['DXY', 'JPY', 'CHF'],
      commodities: ['GOLD', 'SILVER'],
      indices: ['SP500', 'NASDAQ', 'DOW']
    }
  }
};

class EconomicIndicatorsService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 300000; // 5 minutes cache
  
  // Data source management
  private currentDataSource: DataSource = 'yahoo';
  private failureCount = new Map<DataSource, number>();
  private readonly MAX_FAILURES = 3;
  private circuitBreakerOpen = new Map<DataSource, boolean>();
  private lastFailureTime = new Map<DataSource, number>();
  private readonly CIRCUIT_BREAKER_TIMEOUT = 300000; // 5 minutes
  
  // Real data enabled - ACTIVE
  private readonly REAL_DATA_ENABLED = true;

  // Circuit breaker management
  private checkCircuitBreaker(source: DataSource): boolean {
    const isOpen = this.circuitBreakerOpen.get(source) || false;
    const lastFailure = this.lastFailureTime.get(source) || 0;
    
    if (isOpen && Date.now() - lastFailure > this.CIRCUIT_BREAKER_TIMEOUT) {
      this.circuitBreakerOpen.set(source, false);
      this.failureCount.set(source, 0);
      return false;
    }
    
    return isOpen;
  }

  private recordFailure(source: DataSource): void {
    const failures = (this.failureCount.get(source) || 0) + 1;
    this.failureCount.set(source, failures);
    this.lastFailureTime.set(source, Date.now());
    
    if (failures >= this.MAX_FAILURES) {
      this.circuitBreakerOpen.set(source, true);
      console.warn(`Circuit breaker opened for ${source} after ${failures} failures`);
    }
  }

  private switchToNextDataSource(): void {
    const sources: DataSource[] = ['yahoo', 'investpy', 'mock'];
    const currentIndex = sources.indexOf(this.currentDataSource);
    const nextIndex = (currentIndex + 1) % sources.length;
    this.currentDataSource = sources[nextIndex];
    console.log(`Switched to data source: ${this.currentDataSource}`);
  }

  // Fetch data from Yahoo Finance
  private async fetchFromYahooFinance(symbol: string): Promise<any> {
    const indicator = ECONOMIC_INDICATORS[symbol as keyof typeof ECONOMIC_INDICATORS];
    if (!indicator?.yahoo) {
      throw new Error(`Yahoo symbol not found for ${symbol}`);
    }
    
    try {
      const quote = await yahooFinanceService.getQuote(indicator.yahoo);
      return {
        symbol: indicator.yahoo,
        price: quote.regularMarketPrice,
        change: quote.regularMarketChange,
        changePercent: quote.regularMarketChangePercent
      };
    } catch (error) {
      console.warn(`Yahoo Finance error for ${symbol}:`, error);
      throw error;
    }
  }

  // Fetch data from Investpy
  private async fetchFromInvestpy(symbol: string): Promise<any> {
    const indicator = ECONOMIC_INDICATORS[symbol as keyof typeof ECONOMIC_INDICATORS];
    if (!indicator?.investpy) {
      throw new Error(`Investpy symbol not found for ${symbol}`);
    }
    
    try {
      const quote = await investpyService.getQuote(indicator.investpy);
      return {
        symbol: indicator.investpy,
        price: quote.price,
        change: quote.change,
        changePercent: quote.changePercent
      };
    } catch (error) {
      console.warn(`Investpy error for ${symbol}:`, error);
      throw error;
    }
  }

  // Fetch data from current source
  private async fetchFromCurrentSource(symbol: string): Promise<any> {
    if (this.checkCircuitBreaker(this.currentDataSource)) {
      this.switchToNextDataSource();
    }
    
    try {
      let data;
      
      switch (this.currentDataSource) {
        case 'yahoo':
          data = await this.fetchFromYahooFinance(symbol);
          break;
        case 'investpy':
          data = await this.fetchFromInvestpy(symbol);
          break;
        case 'mock':
          return null; // Will use mock data
        default:
          throw new Error(`Unknown data source: ${this.currentDataSource}`);
      }
      
      // Reset failure count on success
      this.failureCount.set(this.currentDataSource, 0);
      return data;
    } catch (error) {
      this.recordFailure(this.currentDataSource);
      throw error;
    }
  }

  // Calculate impact level based on change
  calculateImpact(changePercent: number): 'High' | 'Medium' | 'Low' {
    const absChange = Math.abs(changePercent);
    if (absChange > 2) return 'High';
    if (absChange > 0.5) return 'Medium';
    return 'Low';
  }

  // Calculate direction based on indicator type and change
  calculateDirection(symbol: string, changePercent: number): 'Bullish' | 'Bearish' | 'Neutral' {
    if (Math.abs(changePercent) < 0.1) return 'Neutral';
    
    // For VIX and USEPU, higher values are bearish for markets
    if (symbol === 'VX1' || symbol === 'USEPU') {
      return changePercent > 0 ? 'Bearish' : 'Bullish';
    }
    
    // For yield futures, higher yields can be bearish for bonds but bullish for USD
    if (symbol === '10Y1' || symbol === 'ZN1') {
      return changePercent > 0 ? 'Bearish' : 'Bullish';
    }
    
    // For DXY, higher values are bullish for USD
    if (symbol === 'DXY') {
      return changePercent > 0 ? 'Bullish' : 'Bearish';
    }
    
    return changePercent > 0 ? 'Bullish' : 'Bearish';
  }

  // Get mock data for economic indicators
  getMockIndicatorsData(): EconomicIndicator[] {
    return [
      {
        symbol: '10Y1',
        name: '10-Year Yield Futures',
        value: 4.285,
        change: 0.045,
        changePercent: 1.06,
        impact: 'Medium',
        direction: 'Bearish',
        description: 'Rising yields indicate tightening monetary policy',
        lastUpdated: new Date().toISOString(),
        correlations: ECONOMIC_INDICATORS['10Y1'].correlations
      },
      {
        symbol: 'ZN1',
        name: 'Futures T-Note 10 Years',
        value: 110.25,
        change: -0.75,
        changePercent: -0.68,
        impact: 'Medium',
        direction: 'Bullish',
        description: 'T-Note futures declining as yields rise',
        lastUpdated: new Date().toISOString(),
        correlations: ECONOMIC_INDICATORS['ZN1'].correlations
      },
      {
        symbol: 'DXY',
        name: 'US Dollar Index',
        value: 103.45,
        change: 0.25,
        changePercent: 0.24,
        impact: 'Low',
        direction: 'Bullish',
        description: 'Dollar strength continues amid Fed policy expectations',
        lastUpdated: new Date().toISOString(),
        correlations: ECONOMIC_INDICATORS['DXY'].correlations
      },
      {
        symbol: 'VX1',
        name: 'CBOE Volatility Index',
        value: 18.45,
        change: -1.25,
        changePercent: -6.34,
        impact: 'High',
        direction: 'Bullish',
        description: 'Volatility declining, indicating market confidence',
        lastUpdated: new Date().toISOString(),
        correlations: ECONOMIC_INDICATORS['VX1'].correlations
      },
      {
        symbol: 'USEPU',
        name: 'Economic Policy Uncertainty Index',
        value: 145.8,
        change: 8.2,
        changePercent: 5.96,
        impact: 'High',
        direction: 'Bearish',
        description: 'Rising policy uncertainty affecting market sentiment',
        lastUpdated: new Date().toISOString(),
        correlations: ECONOMIC_INDICATORS['USEPU'].correlations
      }
    ];
  }

  // Get real-time economic indicators data using current data source
  async getEconomicIndicators(): Promise<EconomicIndicator[]> {
    const cacheKey = 'economic_indicators';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    if (!this.REAL_DATA_ENABLED || this.currentDataSource === 'mock') {
      const mockData = this.getMockIndicatorsData();
      this.cache.set(cacheKey, { data: mockData, timestamp: Date.now() });
      return mockData;
    }

    try {
      const indicatorPromises = Object.entries(ECONOMIC_INDICATORS).map(async ([key, config]) => {
        try {
          const data = await this.fetchFromCurrentSource(key);
          
          if (data) {
            const currentValue = data.price || 0;
            const change = data.change || 0;
            const changePercent = data.changePercent || 0;

            return {
              symbol: key,
              name: config.name,
              value: Number(currentValue.toFixed(2)),
              change: Number(change.toFixed(2)),
              changePercent: Number(changePercent.toFixed(2)),
              impact: this.calculateImpact(changePercent),
              direction: this.calculateDirection(key, changePercent),
              description: config.description,
              lastUpdated: new Date().toISOString(),
              correlations: config.correlations
            };
          }
          
          return null;
        } catch (error) {
          console.warn(`Failed to fetch data for ${key} from ${this.currentDataSource}:`, error);
          return null;
        }
      });

      const results = await Promise.all(indicatorPromises);
      const validResults = results.filter(Boolean) as EconomicIndicator[];
      
      // If we got some real data, use it; otherwise try next source or fall back to mock
      if (validResults.length === 0 && (this.currentDataSource as DataSource) !== 'mock') {
        this.switchToNextDataSource();
        return this.getEconomicIndicators(); // Recursive call with new source
      }
      
      const finalData = validResults.length > 0 ? validResults : this.getMockIndicatorsData();
      
      console.log(`${this.currentDataSource} economic indicators: ${validResults.length} real, ${finalData.length - validResults.length} mock`);
      
      this.cache.set(cacheKey, { data: finalData, timestamp: Date.now() });
      return finalData;
    } catch (error) {
      console.warn(`Error fetching economic indicators from ${this.currentDataSource}, using mock data:`, error);
      const mockData = this.getMockIndicatorsData();
      this.cache.set(cacheKey, { data: mockData, timestamp: Date.now() });
      return mockData;
    }
  }

  // Get market correlations for a specific asset
  getAssetCorrelations(assetSymbol: string): MarketCorrelation[] {
    const correlations: MarketCorrelation[] = [];
    
    Object.entries(ECONOMIC_INDICATORS).forEach(([indicatorSymbol, config]) => {
      const { currencies, commodities, indices } = config.correlations;
      
      let correlationStrength = 0;
      let description = '';
      
      if (currencies.includes(assetSymbol)) {
        if (indicatorSymbol === 'DXY') {
          correlationStrength = assetSymbol === 'DXY' ? 1 : -0.8;
          description = assetSymbol === 'DXY' ? 'Direct correlation' : 'Inverse correlation with USD strength';
        } else if (indicatorSymbol === 'VX1') {
          correlationStrength = ['JPY', 'CHF'].includes(assetSymbol) ? 0.6 : -0.4;
          description = 'Safe haven currencies benefit from volatility';
        }
      } else if (commodities.includes(assetSymbol)) {
        if (indicatorSymbol === 'DXY') {
          correlationStrength = -0.7;
          description = 'Commodities typically inverse to USD strength';
        } else if (indicatorSymbol === 'VX1') {
          correlationStrength = assetSymbol === 'GOLD' ? 0.5 : -0.3;
          description = 'Gold benefits from uncertainty, other commodities suffer';
        }
      } else if (indices.includes(assetSymbol)) {
        if (indicatorSymbol === 'VX1') {
          correlationStrength = -0.9;
          description = 'Strong inverse correlation with equity indices';
        } else if (indicatorSymbol === '10Y1') {
          correlationStrength = -0.6;
          description = 'Rising yields pressure equity valuations';
        }
      }
      
      if (correlationStrength !== 0) {
        correlations.push({
          indicator: indicatorSymbol,
          asset: assetSymbol,
          correlation: correlationStrength,
          strength: Math.abs(correlationStrength) > 0.7 ? 'Strong' : 
                   Math.abs(correlationStrength) > 0.4 ? 'Moderate' : 'Weak',
          description
        });
      }
    });
    
    return correlations;
  }

  // Generate market insights based on economic indicators
  async generateMarketInsights(): Promise<EconomicInsight[]> {
    const indicators = await this.getEconomicIndicators();
    const insights: EconomicInsight[] = [];
    
    // VIX Analysis - Always generate insight
    const vix = indicators.find(i => i.symbol === 'VX1');
    if (vix) {
      if (vix.value > 25) {
        insights.push({
          title: 'High Market Volatility Signal - Risk-Off Mode',
          description: 'VIX above 25 indicates elevated fear. SELL risk assets: CRUDE OIL, GASOLINE futures. Consider GOLD as safe haven. Avoid high-beta commodities.',
          indicators: ['VX1'],
          impact: 'Negative',
          confidence: 85,
          timeframe: 'Short-term'
        });
      } else if (vix.value < 15) {
        insights.push({
          title: 'Low Volatility Environment - Risk-On Mode',
          description: 'VIX below 15 suggests market complacency. BUY growth-sensitive futures: CRUDE OIL, GASOLINE, COPPER. Reduce GOLD exposure.',
          indicators: ['VX1'],
          impact: 'Positive',
          confidence: 75,
          timeframe: 'Short-term'
        });
      } else {
        insights.push({
          title: 'Moderate Volatility Regime - Selective Trading',
          description: 'VIX in normal range (15-25). HOLD current positions. Monitor CRUDE OIL for technical breakouts. GOLD remains range-bound.',
          indicators: ['VX1'],
          impact: 'Neutral',
          confidence: 70,
          timeframe: 'Short-term'
        });
      }
    }
    
    // Dollar Strength Analysis - Always generate insight
    const dxy = indicators.find(i => i.symbol === 'DXY');
    if (dxy) {
      if (dxy.changePercent > 0.5) {
        insights.push({
          title: 'USD Strength Signal - Commodity Headwinds',
          description: 'Rising dollar pressures all commodities. SELL CRUDE OIL, GOLD, SILVER, COPPER futures. Dollar-denominated assets under pressure.',
          indicators: ['DXY'],
          impact: 'Negative',
          confidence: 80,
          timeframe: 'Short-term'
        });
      } else if (dxy.changePercent < -0.5) {
        insights.push({
          title: 'USD Weakness Signal - Commodity Tailwinds',
          description: 'Weakening dollar supports all commodity prices. BUY CRUDE OIL, GASOLINE, GOLD, SILVER, COPPER futures. Broad commodity rally expected.',
          indicators: ['DXY'],
          impact: 'Positive',
          confidence: 80,
          timeframe: 'Short-term'
        });
      } else {
        insights.push({
          title: 'USD Consolidation - Mixed Commodity Outlook',
          description: 'Dollar trading sideways. HOLD commodity positions. Focus on individual fundamentals for CRUDE OIL and GOLD.',
          indicators: ['DXY'],
          impact: 'Neutral',
          confidence: 65,
          timeframe: 'Short-term'
        });
      }
    }
    
    // Yield Analysis - Always generate insight
    const yield10Y = indicators.find(i => i.symbol === '10Y1');
    if (yield10Y) {
      if (yield10Y.changePercent > 1) {
        insights.push({
          title: 'Rising Yields Signal - Precious Metals Pressure',
          description: 'Significant yield increase suggests hawkish Fed policy. SELL GOLD, SILVER futures. Higher yields increase opportunity cost of non-yielding assets.',
          indicators: ['10Y1', 'ZN1'],
          impact: 'Negative',
          confidence: 85,
          timeframe: 'Medium-term'
        });
      } else if (yield10Y.changePercent < -1) {
        insights.push({
          title: 'Falling Yields Signal - Precious Metals Rally',
          description: 'Declining yields indicate flight-to-safety. BUY GOLD, SILVER futures. Lower yields reduce opportunity cost of holding precious metals.',
          indicators: ['10Y1', 'ZN1'],
          impact: 'Positive',
          confidence: 85,
          timeframe: 'Medium-term'
        });
      } else {
        insights.push({
          title: 'Stable Yield Environment - Range-Bound Metals',
          description: 'Yields trading in range. HOLD GOLD and SILVER positions. Wait for clearer directional signals in interest rates.',
          indicators: ['10Y1', 'ZN1'],
          impact: 'Neutral',
          confidence: 70,
          timeframe: 'Medium-term'
        });
      }
    }
    
    // Policy Uncertainty Analysis - Always generate insight
    const uncertainty = indicators.find(i => i.symbol === 'USEPU');
    if (uncertainty) {
      if (uncertainty.changePercent > 3) {
        insights.push({
          title: 'Rising Policy Uncertainty - Defensive Assets',
          description: 'Increased uncertainty dampens investment appetite. BUY GOLD as safe haven. SELL risk assets like CRUDE OIL, GASOLINE.',
          indicators: ['USEPU'],
          impact: 'Negative',
          confidence: 75,
          timeframe: 'Medium-term'
        });
      } else if (uncertainty.changePercent < -3) {
        insights.push({
          title: 'Declining Policy Uncertainty - Risk Assets Rally',
          description: 'Reduced uncertainty supports risk appetite. BUY CRUDE OIL, GASOLINE, COPPER futures. Reduce defensive GOLD allocation.',
          indicators: ['USEPU'],
          impact: 'Positive',
          confidence: 75,
          timeframe: 'Medium-term'
        });
      } else {
        insights.push({
          title: 'Stable Policy Environment - Balanced Allocation',
          description: 'Policy uncertainty unchanged. HOLD current commodity mix. Balance between growth assets (CRUDE OIL) and defensive assets (GOLD).',
          indicators: ['USEPU'],
          impact: 'Neutral',
          confidence: 65,
          timeframe: 'Medium-term'
        });
      }
    }
    
    // Overall Market Signal - Composite analysis
    const bullishSignals = insights.filter(i => i.impact === 'Positive').length;
    const bearishSignals = insights.filter(i => i.impact === 'Negative').length;
    const neutralSignals = insights.filter(i => i.impact === 'Neutral').length;
    
    let overallImpact: 'Positive' | 'Negative' | 'Neutral';
    let overallConfidence: number;
    let overallDescription: string;
    
    if (bullishSignals > bearishSignals) {
      overallImpact = 'Positive';
      overallConfidence = Math.min(90, 60 + (bullishSignals * 10));
      overallDescription = `${bullishSignals} bullish vs ${bearishSignals} bearish signals. Favorable environment for risk assets.`;
    } else if (bearishSignals > bullishSignals) {
      overallImpact = 'Negative';
      overallConfidence = Math.min(90, 60 + (bearishSignals * 10));
      overallDescription = `${bearishSignals} bearish vs ${bullishSignals} bullish signals. Defensive positioning recommended.`;
    } else {
      overallImpact = 'Neutral';
      overallConfidence = 60;
      overallDescription = `Balanced signals (${bullishSignals} bullish, ${bearishSignals} bearish). Mixed market environment.`;
    }
    
    insights.unshift({
      title: '🎯 Overall Market Signal',
      description: overallDescription,
      indicators: ['VX1', 'DXY', '10Y1', 'USEPU'],
      impact: overallImpact,
      confidence: overallConfidence,
      timeframe: 'Short-term'
    });
    
    return insights;
  }

  // Start real-time updates for economic indicators
  startRealTimeUpdates(callback: (data: EconomicIndicator[]) => void, interval = 300000) {
    const updateData = async () => {
      const data = await this.getEconomicIndicators();
      console.log(`Economic indicators updated from ${this.currentDataSource}`);
      callback(data);
    };

    // Initial load
    updateData();
    
    // Set up interval (5 minutes)
    return setInterval(updateData, interval);
  }

  // Data source management methods
  getCurrentDataSource(): DataSource {
    return this.currentDataSource;
  }

  switchDataSource(source: DataSource): void {
    this.currentDataSource = source;
    this.cache.clear(); // Clear cache when switching sources
    console.log(`Manually switched to data source: ${source}`);
  }

  getDataSourceStatus(): { source: DataSource; available: boolean; failures: number }[] {
    const sources: DataSource[] = ['yahoo', 'investpy', 'mock'];
    return sources.map(source => ({
      source,
      available: !this.checkCircuitBreaker(source),
      failures: this.failureCount.get(source) || 0
    }));
  }

  resetAllCircuitBreakers(): void {
    this.circuitBreakerOpen.clear();
    this.failureCount.clear();
    this.lastFailureTime.clear();
    this.currentDataSource = 'yahoo'; // Reset to primary source
    console.log('All circuit breakers reset, switched back to Yahoo Finance');
  }
}

export const economicIndicatorsService = new EconomicIndicatorsService();
export default economicIndicatorsService;